/**
 * ArchiveTeamModal - Confirmation dialog with mandatory reason (DRR-061)
 *
 * FERP Append-Only: Teams are archived, never deleted.
 * Mandatory reason for HIPAA/SOX/SOC2 compliance.
 */

import { useState, useCallback } from 'react';
import { Modal, Stack, Text, Textarea, Group, Button, Alert } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import type { GqlTeam } from '@ferp/workflow-forms';

interface ArchiveTeamModalProps {
  team: GqlTeam | null;
  opened: boolean;
  onClose: () => void;
  onArchive: (code: string, reason: string) => Promise<void>;
  loading?: boolean;
}

export function ArchiveTeamModal({
  team,
  opened,
  onClose,
  onArchive,
  loading = false,
}: ArchiveTeamModalProps) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleArchive = useCallback(async () => {
    if (!team) return;
    if (!reason.trim()) {
      setError('Archival reason is required for audit compliance.');
      return;
    }
    try {
      setError(null);
      await onArchive(team.code, reason.trim());
      setReason('');
      onClose();
    } catch (e) {
      setError('Failed to archive team. Please try again.');
    }
  }, [team, reason, onArchive, onClose]);

  const handleClose = useCallback(() => {
    setReason('');
    setError(null);
    onClose();
  }, [onClose]);

  if (!team) return null;

  return (
    <Modal opened={opened} onClose={handleClose} title="Archive Team" size="md">
      <Stack gap="md">
        <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light">
          Archiving <strong>{team.name}</strong> ({team.code}) will set it to inactive.
          This action creates an audit trail entry and cannot be undone without admin intervention.
        </Alert>

        <Textarea
          label="Archival Reason"
          description="Required for HIPAA/SOX/SOC2 compliance"
          placeholder="e.g., Team restructured into two smaller squads..."
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          minRows={3}
          required
          error={error}
        />

        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleArchive}
            loading={loading}
            disabled={!reason.trim()}
          >
            Archive Team
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
